import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import AppBar from './AppBar';

function Profile() {
  const [topics, setTopics] = useState([]);
  const [newTopicName, setNewTopicName] = useState('');
  const [editingTopic, setEditingTopic] = useState(null);
  const [editedTopicName, setEditedTopicName] = useState('');

  // Fetch topics for the current user
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const topicsQuery = query(
      collection(db, "topics"),
      where("uid", "==", currentUser.uid)
    );
    
    const unsubscribe = onSnapshot(topicsQuery, (snapshot) => {
      const topicsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTopics(topicsData);
    });

    return () => unsubscribe();
  }, []);

  const handleAddTopic = async (e) => {
    e.preventDefault();
    if (!newTopicName.trim()) return;

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error("No user is signed in");
        return;
      }

      await addDoc(collection(db, "topics"), {
        name: newTopicName.trim(),
        createdAt: new Date().toISOString(),
        uid: currentUser.uid
      });

      setNewTopicName(''); // Clear input after adding
    } catch (error) {
      console.error("Error adding topic:", error);
    }
  };

  const startEditing = (topic) => {
    setEditingTopic(topic.id);
    setEditedTopicName(topic.name);
  };

  const handleEditTopic = async (topicId) => {
    if (!editedTopicName.trim() || editedTopicName.trim() === topics.find(t => t.id === topicId)?.name) {
      setEditingTopic(null);
      return;
    }

    try {
      const topicRef = doc(db, "topics", topicId);
      await updateDoc(topicRef, {
        name: editedTopicName.trim()
      });
      setEditingTopic(null);
    } catch (error) {
      console.error("Error updating topic:", error);
    }
  };

  const handleDeleteTopic = async (topicId) => {
    // Show confirmation dialog
    if (!window.confirm("Are you sure you want to delete this topic? This action cannot be undone.")) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, "topics", topicId));
      // No need to update state as the Firestore listener will handle that
    } catch (error) {
      console.error("Error deleting topic:", error);
    }
  };

  return (
    <div className="flex-col h-[100dvh] overflow-hidden">
      <div className="flex bg-gray-50 h-[100dvh]">
        <div className="flex-1 max-w-[440px] border-r border-gray-200 bg-white flex flex-col pb-[var(--app-bar-height,120px)]">
          <div className="p-6">
            <h1 className="text-2xl font-semibold mb-6">Profile</h1>
            
            {/* Add new topic form */}
            <div className="mb-8">
              <form onSubmit={handleAddTopic} className="flex gap-2">
                <input
                  type="text"
                  value={newTopicName}
                  onChange={(e) => setNewTopicName(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="Enter topic name"
                />
                <button
                  type="submit"
                  className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Add
                </button>
              </form>
            </div>
            
            {/* Topics section */}
            <div>
              {topics.length === 0 ? (
                <p className="text-gray-500">No topics created yet.</p>
              ) : (
                <ul className="space-y-2">
                  {topics.map(topic => (
                    <li 
                      key={topic.id} 
                      className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      {editingTopic === topic.id ? (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={editedTopicName}
                            onChange={(e) => setEditedTopicName(e.target.value)}
                            className="flex-1 border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-black"
                          />
                          <button
                            onClick={() => handleEditTopic(topic.id)}
                            className="bg-black text-white px-3 py-1 rounded-lg text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingTopic(null)}
                            className="bg-gray-200 px-3 py-1 rounded-lg text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-between items-center">
                          <span className="font-medium">{topic.name}</span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => startEditing(topic)}
                              className="text-gray-600 hover:text-black"
                              title="Edit topic"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteTopic(topic.id)}
                              className="text-gray-600 hover:text-red-600"
                              title="Delete topic"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 6h18"></path>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AppBar */}
      <AppBar
        className="fixed bottom-0 left-0 right-0"
        hideInput={true}
      />
    </div>
  );
}

export default Profile; 